import React, { useEffect, useState } from 'react';
import { CircularProgress, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { UserInfoModel } from '../../models/UserInfoModel';
import { actions as UserAction } from '../../store/redux/UserInfoRedux';
import { actions as VerificationAction } from '../../store/redux/UserVerificationRedux';
import { actions as OTPAction } from '../../store/redux/OTPConfirmRedux';
import TemplateComponent from '../../component/template/TemplateComponent';
import OtpInput from 'react-otp-input';
import { OTPConfirmRequest, UserManageVerifyRequest } from '../../models/RequestModel';
import { AcnColorTheme } from '../../assets/css/AcnColorTheme';
import axios from 'axios';
import { ApiConfig, ServiceApi } from '../../service/ServiceApi';
import { VerificationResponse } from '../../models/ResponseModel';
import { NavTo } from '../../utils/AppManager';
import { Path } from '../../utils/Constant';
import DynamicTitle from '../../component/DynamicTitle';
import { fontUnit, heightUnit, padGinUnit, widthUnit } from '../../utils/DeviceManager';
const iOS = require('is-ios');

const MAXIMUM_OTP_FAILED = 3;
const DEFAULT_BLOCKING_TIME = 60;

export function OTPForManageScreen() {
  const dispatch = useDispatch();
  const [userInfo, accessToken, unblockTime] = useSelector((state: RootState) => [
    state.userInfoReducer.userInfo,
    state.userVerificationReducer.accessToken,
    state.userVerificationReducer.accessToken?.unblockTime,
  ]);
  const [otpValue, setOtpValue] = useState({ otp: '' });
  const [message, setMessage] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestTime, setRequestTime] = useState(0);
  const [failTime, setFailTime] = useState(1);
  const [disableRequestOtp, setDisableRequestOtp] = useState(true);
  const [intervalId, setIntervalId] = useState<ReturnType<typeof setInterval> | null>(null);
  const [timer, setTimer] = useState(0);
  const [isDisabledFillingOTP, setIsDisabledFillingOTP] = useState(false);

  const OTP_INVALID = `รหัส OTP ไม่ถูกต้อง ลองใหม่ได้อีก ${MAXIMUM_OTP_FAILED - failTime} ครั้ง`;
  const OTP_INVALID_OVER_MAX_LIMIT = 'ท่านได้ใส่รหัส OTP ผิดเกิน 3 ครั้ง\nกรุณาลองใหม่ในอีก 30 นาที';
  const screenWidth: number = window.innerWidth;
  const hasVerificationToken = typeof accessToken?.accessToken === 'string' && !!accessToken.accessToken.trim();
  const hasUserToken = typeof userInfo.accessToken === 'string' && !!userInfo.accessToken.trim();

  const resetMissingToken = () => {
    setMessage('ไม่พบโทเค็น กรุณาเริ่มขั้นตอนยืนยันตัวตนใหม่อีกครั้ง');
    resetStateWithDisabledReqOtp(true);
  };

  const resetStateWithDisabledReqOtp = (disabledReqOtp: boolean) => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    setIntervalId(null);
    setTimer(0);
    setDisableRequestOtp(disabledReqOtp);
    setOtpValue({ otp: '' });
    setIsDisabledFillingOTP(true);
  };

  useEffect(() => {
    document.title = 'สมัครใช้งาน\u200E\u200E\u200E\u200E\u200E\u200E\u200E\u200E';
  }, []);

  useEffect(() => {
    if (hasVerificationToken) {
      const newInfo: UserInfoModel = { ...userInfo, accessToken: accessToken.accessToken };
      UserAction.setUserInfo(dispatch, newInfo).then(() => {
        setOtpValue({ otp: '' });
        setIsDisabledFillingOTP(false);
      });
    } else {
      if (!isRequesting || !hasVerificationToken || !hasUserToken) {
        resetMissingToken();
      }
    }
  }, [accessToken]);

  useEffect(() => {
    if (isValidOTP()) {
      onConfirm();
    }
  }, [otpValue]);

  useEffect(() => {
    // Restore the remaining cooldown on mount, including reloads with a valid token.
    if (hasVerificationToken) {
      delayOTP(unblockTime);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  const delayOTP = (until?: string) => {
    if (intervalId) clearInterval(intervalId);
    const parsedUntil = until ? new Date(until).getTime() : NaN;
    const cooldownUntil = Number.isFinite(parsedUntil) ? parsedUntil : Date.now() + DEFAULT_BLOCKING_TIME * 1000;
    const remainingSeconds = () => Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
    const seconds = remainingSeconds();
    setTimer(seconds);
    setDisableRequestOtp(seconds > 0);
    if (seconds === 0) {
      setIntervalId(null);
      return;
    }

    const intervalRef = setInterval(() => {
      const remaining = remainingSeconds();
      setTimer(remaining);
      if (remaining === 0) {
        clearInterval(intervalRef);
        setDisableRequestOtp(false);
        setIntervalId(null);
      }
    }, 1000);
    setIntervalId(intervalRef);
  };

  const isValidOTP = () => {
    return otpValue.otp.length === 6;
  };

  const resetOTPFailedTime = () => {
    if (failTime >= MAXIMUM_OTP_FAILED) {
      setFailTime(0);
    }
  };

  const onConfirm = () => {
    if (!hasVerificationToken || !hasUserToken) {
      resetMissingToken();
      return;
    }
    if (isRequesting) return;
    if (isValidOTP()) {
      const request: OTPConfirmRequest = { accessToken: userInfo.accessToken, otp: otpValue.otp };
      setIsRequesting(true);
      axios
        .post(ServiceApi.OTP_CONFIRM, request, ApiConfig)
        .then((res) => {
          const response: VerificationResponse = res.data;
          OTPAction.setOTPConfirm(dispatch, response.accessToken == userInfo.accessToken).then(() => {
            setIsRequesting(false);
          });
          return res;
        })
        .then((data) => {
          setIsRequesting(false);
          if (data.data.accessToken === userInfo.accessToken) {
            NavTo(Path.mng_chg.replaceAll(':token', userInfo.accessToken));
          } else {
            setMessage('ขออภัย OTP หมดอายุ กรุณาลองใหม่ในอีกสักครู่');
          }
        })
        .catch((error) => {
          setOtpValue({ otp: '' });
          setIsRequesting(false);
          const errorStr = JSON.stringify(error);
          //httpStatusCode = 400, 409, ...
          const httpStatusCode = error.response.status;
          //errorCode = BU005, BU006, ..
          const errorDataCode = error.response.data.code;

          if (httpStatusCode >= 500) {
            // internal server error or server unavailable
            NavTo(Path.mng_acc.replaceAll(':token', userInfo.accessToken));
          } else if (httpStatusCode == 409 && errorDataCode == 'BU008') {
            // error message (BU008)
            // Otp verification not completed ,Limited
            // input incorrect OTP for the fourth time
            setMessage(OTP_INVALID_OVER_MAX_LIMIT);
            resetStateWithDisabledReqOtp(false);
          } else {
            // http status 400, error code BU005 (input incorrect OTP)
            if (failTime >= MAXIMUM_OTP_FAILED) {
              setMessage(OTP_INVALID_OVER_MAX_LIMIT);
              resetStateWithDisabledReqOtp(false);
            } else {
              setFailTime(failTime + 1);
              setMessage(OTP_INVALID);
            }
          }
        });
    }
  };

  const requestOtp = () => {
    if (!hasVerificationToken || !hasUserToken) {
      resetMissingToken();
      return;
    }
    if (disableRequestOtp || isRequesting) return;
    setIsRequesting(true);
    const request: UserManageVerifyRequest = {
      recipientPhoneNumber: userInfo.mobile,
      token: userInfo.accessToken,
    };
    VerificationAction.postVerification(dispatch, request)
      .then((response) => {
        setMessage('');
        setIsRequesting(false);
        setIsDisabledFillingOTP(false);
        resetOTPFailedTime();
        setRequestTime(requestTime + 1);
        delayOTP(response.unblockTime);
      })
      .catch((error) => {
        setIsRequesting(false);
        setIsDisabledFillingOTP(false);
        resetOTPFailedTime();
        const blockedUntil = error.response?.data?.unblockTime;
        if (error.response?.status === 400 && blockedUntil) {
          setMessage('กรุณารอสักครู่ก่อนขอรหัส OTP ใหม่อีกครั้ง');
          delayOTP(blockedUntil);
        } else {
          setMessage('ไม่สามารถส่งรหัส OTP ได้ กรุณาลองใหม่อีกครั้ง');
        }
      });
  };

  const markText = (str: String) => {
    return '••• ••• ' + str.substr(str.length - 4, str.length);
  };

  const onLoading = () => {
    return (
      <Typography
        component="div"
        align={'center'}
        style={{
          position: 'relative',
          width: '100%',
          alignContent: 'center',
          paddingTop: padGinUnit(200),
        }}
      >
        <CircularProgress color="primary" style={{ alignSelf: 'center' }} />
      </Typography>
    );
  };

  const OTPWidget = () => {
    return (
      <div style={{ overflow: 'none' }}>
        <DynamicTitle title={'ยืนยันตัวตน'} size={fontUnit(24)} isFull={true} align={'left'} />
        <Typography
          align={'left'}
          style={{ fontSize: fontUnit(16), color: AcnColorTheme.black, fontWeight: 'normal', marginTop: padGinUnit(8) }}
        >
          เราได้ส่งรหัสยืนยันตัวตน (OTP) ไปยังหมายเลขโทรศัพท์มือถือ {markText(userInfo.mobile)} เรียบร้อยแล้ว
          กรุณากรอกรหัสที่ได้เพื่อยืนยันตัวตน
        </Typography>
        <div
          style={{
            maxWidth: window.innerWidth,
            color: AcnColorTheme.black,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: padGinUnit(40),
            marginBottom: padGinUnit(16),
          }}
        >
          <OtpInput
            value={otpValue.otp}
            onChange={(event: any) => {
              setMessage('');
              setOtpValue({ otp: event });
            }}
            numInputs={6}
            isDisabled={isRequesting || isDisabledFillingOTP || !hasVerificationToken || !hasUserToken}
            inputStyle={{
              fontSize: fontUnit(32),
              width: iOS ? widthUnit(screenWidth / 20) : widthUnit(screenWidth / 9),
              height: iOS ? heightUnit(35) : heightUnit(screenWidth / 10),
              margin: '4px',
              borderTop: '0px',
              borderLeft: '0px',
              borderRight: '0px',
              borderBottom: '2px solid',
              borderColor: AcnColorTheme.black,
              outline: 'none',
              borderRadius: 0,
              background: 'transparent',
              color: AcnColorTheme.black,
              fontStyle: 'medium',
            }}
            containerStyle={{
              padding: '16px',
            }}
            isInputNum={true}
            shouldAutoFocus={false}
            errorStyle={{
              borderColor: AcnColorTheme.secondaryRed,
            }}
            hasErrored={message !== ''}
          />
        </div>
        {message && (
          <Typography
            align="left"
            style={{
              color: AcnColorTheme.secondaryRed,
              fontSize: fontUnit(14),
              paddingBottom: padGinUnit(16),
            }}
          >
            {message}
          </Typography>
        )}

        <Typography align="left" style={{ fontSize: fontUnit(16), color: AcnColorTheme.black }}>
          {userInfo.accessToken ? `รหัสอ้างอิง: ${userInfo.accessToken.substr(0, 5)}` : 'กรุณาขอ OTP ใหม่อีกครั้ง'}
        </Typography>

        {requestTime < 2 ? (
          <Typography
            align="left"
            style={{ marginTop: padGinUnit(4), fontSize: fontUnit(16), color: AcnColorTheme.black }}
          >
            ยังไม่ได้รับรหัส&nbsp;
            <span
              aria-disabled={disableRequestOtp}
              onClick={requestOtp}
              style={{
                fontFamily: 'Prompt-Medium',
                fontStyle: 'normal',
                color: disableRequestOtp ? AcnColorTheme.primaryPurple : AcnColorTheme.lightBlue,
                cursor: disableRequestOtp ? 'default' : 'pointer',
              }}
            >
              ส่งรหัสใหม่อีกครั้ง{disableRequestOtp && timer > 0 ? `(${timer})` : ''}
            </span>
          </Typography>
        ) : (
          <div />
        )}
        <div style={{ height: !iOS ? '250px' : '' }} />
      </div>
    );
  };

  return (
    <TemplateComponent
      content={isRequesting ? onLoading() : OTPWidget()}
      isCanNext={isValidOTP()}
      nextText={'ยืนยัน'}
    />
  );
}
